import { useMutation, useQueryClient } from "@tanstack/react-query";
import { generatePath } from "react-router";
import { useToast } from "@chakra-ui/react";
import apiClient from "services/ApiClient";
import { API_ROUTES } from "definitions/constants/routeConstants";
import type { GridTask, TaskGridResponse, TaskStatus } from "../types";
import { taskGridKeys } from "./queries";

type TaskStatusMutationContext = { previous?: TaskGridResponse };

export function useTaskStatusMutation() {
  const queryClient = useQueryClient();
  const toast = useToast();
  return useMutation<GridTask, Error, { taskId: number; status: TaskStatus }, TaskStatusMutationContext>({
    mutationFn: async ({ taskId, status }) => {
      const path = generatePath(API_ROUTES.tasks.show, { id: String(taskId) });
      return apiClient.put<GridTask>(path, { task: { status } });
    },
    onMutate: async ({ taskId, status }): Promise<TaskStatusMutationContext> => {
      await queryClient.cancelQueries({ queryKey: taskGridKeys.grid() });
      const previous = queryClient.getQueryData<TaskGridResponse>(taskGridKeys.grid());
      if (previous) {
        queryClient.setQueryData<TaskGridResponse>(taskGridKeys.grid(), {
          ...previous,
          tasks: previous.tasks.map((t) => (t.id === taskId ? { ...t, status } : t)),
        });
      }
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) queryClient.setQueryData(taskGridKeys.grid(), ctx.previous);
      toast({ title: "Failed to update task", status: "error" });
    },
    onSuccess: () => {
      // No success toast — the cell flips state, that's confirmation enough.
      // Only errors warrant a toast (handled in onError).
      queryClient.invalidateQueries({ queryKey: taskGridKeys.grid() });
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
    },
  });
}

export {
  taskGridKeys,
  useTaskGridQuery,
  useTaskGridPreferencesQuery,
  useUpdateTaskGridPreferencesMutation,
} from "./queries";
export { default as useAutoAddPrefs } from "./useAutoAddPrefs";
export { default as useDensityMode } from "./useDensityMode";
export { default as useNow } from "./useNow";
export { default as useSortMode } from "./useSortMode";
export { default as useTemplateSortMode } from "./useTemplateSortMode";
